import { SupplyInstructionOptions, ProcedureStatus, SchedulingFilter, SamplesDeliveryType } from '@caresend/types';
import update from 'immutability-helper';
import { getOptionalErrors, getBooleanErrors, getByIdErrors, getNumberErrors, getNonEmptyStringErrors, getArrayErrors, getElementTimestampErrors, buildStringEnumErrorsGetter, getPositiveIntegerErrors, getStrictKeysErrors } from './helpers.js';
import { getKeyValidationErrors, appendErrorMessage } from './validation.js';

/**
 * Determine whether a procedure contact passes validation. `userID` should be
 * present on non-custom contacts, and `name` and valid `phone` should be
 * present on custom contacts. These properties should not exist otherwise.
 */

const getProcedureContactErrors = contact => {
  var _a;

  if (contact) {
    if (contact.custom) {
      const isValid = !!contact.name && !!((_a = contact.phone) === null || _a === void 0 ? void 0 : _a.number) && !contact.userID;
      return isValid ? null : 'Expected only name and phone on custom contact';
    } else {
      const isValid = !!contact.userID && !contact.name && !contact.phone;
      return isValid ? null : 'Expected only userID on non-custom contact';
    }
  }

  return 'Expected contact';
};
const isProcedureContactValid = contact => !getProcedureContactErrors(contact);
/**
 * Creates an optional boolean validation key for each instruction
 * (`discardAfterTransfer`, etc.)
 */

const getProcessingInstructionSetValidationKeys = () => {
  const instructionKeys = Object.values(SupplyInstructionOptions);
  const validationKeys = instructionKeys.reduce((validators, instruction) => Object.assign(Object.assign({}, validators), {
    [instruction]: getOptionalErrors(getBooleanErrors)
  }), {});
  return validationKeys;
};

const processingInstructionSetValidationKeys = getProcessingInstructionSetValidationKeys();

const getProcessingInstructionSetErrors = instructions => {
  const keyErrors = getStrictKeysErrors(processingInstructionSetValidationKeys, instructions);
  const valueErrors = getKeyValidationErrors(instructions, processingInstructionSetValidationKeys);
  return appendErrorMessage(keyErrors, valueErrors);
};

const supplyInstructionValidationKeys = {
  quantity: getNumberErrors,
  description: getOptionalErrors(getNonEmptyStringErrors),
  instructions: getOptionalErrors(getArrayErrors(getProcessingInstructionSetErrors)),
  productIDs: getOptionalErrors(getArrayErrors(getNonEmptyStringErrors)),
  supplyID: getNonEmptyStringErrors
};
const supplyInstructionsUpdatedValidationKeys = {
  updatedAt: getNumberErrors,
  updatedBy: getNonEmptyStringErrors
};
const getSupplyInstructionErrors = config => getKeyValidationErrors(config, supplyInstructionValidationKeys);
const getSupplyInstructionsUpdatedErrors = config => getKeyValidationErrors(config, supplyInstructionsUpdatedValidationKeys);
const processingIncludesValidationKeys = {
  centrifuge: getOptionalErrors(getBooleanErrors)
};
const getProcessingIncludesErrors = includes => getKeyValidationErrors(includes, processingIncludesValidationKeys);
const supplyInstructionConfigValidationKeys = {
  placeGroupID: getNonEmptyStringErrors,
  processingIncludes: getOptionalErrors(getProcessingIncludesErrors),
  sampleStability: getNumberErrors,
  samplesDeliveryType: buildStringEnumErrorsGetter(SamplesDeliveryType),
  supplyInstructions: getByIdErrors(getSupplyInstructionErrors),
  supplyInstructionsUpdated: getOptionalErrors(getSupplyInstructionsUpdatedErrors),
  usageInstructions: getOptionalErrors(getNonEmptyStringErrors)
};

const getSupplyInstructionConfigErrors = config => getKeyValidationErrors(config, supplyInstructionConfigValidationKeys);

const procedureValidationKeys = {
  bookingID: true,
  creationTimestamp: true,
  id: true,
  orderID: true,
  products: getByIdErrors(getElementTimestampErrors, 1),
  supplyInstructionConfig: getOptionalErrors(getSupplyInstructionConfigErrors),
  status: true,
  taskID: true,
  taskTypeID: true
};
const partialProcedureValidationKeys = {
  bookingID: getOptionalErrors(getNonEmptyStringErrors),
  creationTimestamp: getOptionalErrors(getNumberErrors),
  id: getOptionalErrors(getNonEmptyStringErrors),
  orderID: getOptionalErrors(getNonEmptyStringErrors),
  products: getOptionalErrors(getByIdErrors(getElementTimestampErrors, 1)),
  supplyInstructionConfig: getOptionalErrors(getSupplyInstructionConfigErrors),
  status: getOptionalErrors(buildStringEnumErrorsGetter(ProcedureStatus)),
  taskID: getOptionalErrors(getNonEmptyStringErrors),
  taskTypeID: getOptionalErrors(getNonEmptyStringErrors)
};
const getProcedureErrors = procedure => getKeyValidationErrors(procedure, procedureValidationKeys);
const getPartialProcedureErrors = procedure => getKeyValidationErrors(procedure, partialProcedureValidationKeys);

const isProcedureValid = procedure => !getProcedureErrors(procedure);

const supplyInstructionConfigOptionValidationKeys = {
  walkIn: getOptionalErrors(getSupplyInstructionConfigErrors),
  mailIn: getOptionalErrors(getSupplyInstructionConfigErrors)
};
const supplyShipmentConfigValidationKeys = {
  supplyArrivalTimestamp: getNumberErrors
};

const getSupplyInstructionConfigOptionValidationErrors = option => getKeyValidationErrors(option, supplyInstructionConfigOptionValidationKeys);

const supplyInstructionsConfigValidationKeys = {
  supplyInstructionsByDeliveryType: getOptionalErrors(getSupplyInstructionConfigOptionValidationErrors),
  samplesDeliveryType: getOptionalErrors(buildStringEnumErrorsGetter(SamplesDeliveryType))
};

const getSupplyInstructionsConfigValidationErrors = config => getKeyValidationErrors(config, supplyInstructionsConfigValidationKeys);

const getSchedulingLayerValidationErrors = schedulingFilters => {
  const validateEnum = buildStringEnumErrorsGetter(SchedulingFilter);

  if (!Array.isArray(schedulingFilters)) {
    return 'Expected an array of scheduling filters, got undefined.';
  }

  for (const filter of schedulingFilters) {
    const error = validateEnum(filter);

    if (error) {
      return error;
    }
  }

  return null;
};

const getSupplyShipmentConfigValidationErrors = config => getKeyValidationErrors(config, supplyShipmentConfigValidationKeys);

const getSupplyInstructionConfigOptionsErrors = options => {
  const {
    collectionBudget,
    schedulingFilters,
    supplyInstructionsByPlaceGroup,
    supplyShipmentConfig
  } = options;
  const collectionBudgetErrors = collectionBudget ? getPositiveIntegerErrors(collectionBudget) : null;
  const schedulingLayerErrors = schedulingFilters ? getSchedulingLayerValidationErrors(schedulingFilters) : null;
  const supplyInstructionsErrors = getByIdErrors(getSupplyInstructionsConfigValidationErrors)(supplyInstructionsByPlaceGroup, options);
  const supplyShipmentConfigErrors = supplyShipmentConfig ? getSupplyShipmentConfigValidationErrors(supplyShipmentConfig) : null;
  const errors = [collectionBudgetErrors, schedulingLayerErrors, supplyInstructionsErrors, supplyShipmentConfigErrors].filter(Boolean).join(', ');
  return errors;
};

class ProcedureCleaner {
  constructor(procedure) {
    this._procedure = procedure;
  }

  get procedure() {
    return this._procedure;
  }

  cleanUpContact() {
    if (this._procedure.contact) {
      if (this._procedure.contact.custom) {
        this._procedure = update(this._procedure, {
          contact: {
            $unset: ['userID']
          }
        });
      } else {
        this._procedure = update(this._procedure, {
          contact: {
            $unset: ['name', 'phone']
          }
        });
      }
    }

    return this;
  }

}
/**
 * Fix data inconsistencies in procedure.
 */


const cleanUpProcedure = procedure => new ProcedureCleaner(procedure).cleanUpContact().procedure;
/**
 * Evaluate and clean object of type Partial<Procedure> with all
 * required properties to resolve a non-partial type
 */

const getValidatedProcedure = procedure => {
  const cleanedProcedure = cleanUpProcedure(procedure);

  if (!isProcedureValid(cleanedProcedure)) {
    throw Error(`Procedure does not pass validation: ${getProcedureErrors(cleanedProcedure)}`);
  }

  return cleanedProcedure;
};

export { cleanUpProcedure, getPartialProcedureErrors, getProcedureContactErrors, getProcedureErrors, getProcessingIncludesErrors, getSupplyInstructionConfigOptionsErrors, getSupplyInstructionErrors, getSupplyInstructionsUpdatedErrors, getValidatedProcedure, isProcedureContactValid };
